/** A binary image over a regular grid, row major, 1 where set */
export type GridMask = {
  width: number;
  height: number;
  data: Uint8Array;
};

/**
 * @param mask The mask to grow
 * @param radius Radius of the square neighborhood, in cells
 * @returns a mask set where any cell of the neighborhood is set, cells outside the grid count as unset
 */
export function dilate(mask: GridMask, radius: number): GridMask {
  const { width, height, data } = mask;
  // Separable sliding window: a row pass, then a column pass on its result
  const rows = new Uint8Array(data.length);
  for (let y = 0; y < height; y++) {
    let count = 0;
    for (let x = -radius; x < width; x++) {
      const entering = x + radius;
      if (entering < width) count += data[y * width + entering];
      const leaving = x - radius - 1;
      if (leaving >= 0) count -= data[y * width + leaving];
      if (x >= 0) rows[y * width + x] = count > 0 ? 1 : 0;
    }
  }
  const out = new Uint8Array(data.length);
  for (let x = 0; x < width; x++) {
    let count = 0;
    for (let y = -radius; y < height; y++) {
      const entering = y + radius;
      if (entering < height) count += rows[entering * width + x];
      const leaving = y - radius - 1;
      if (leaving >= 0) count -= rows[leaving * width + x];
      if (y >= 0) out[y * width + x] = count > 0 ? 1 : 0;
    }
  }
  return { width, height, data: out };
}

/**
 * @param mask The mask to invert
 * @returns the mask with set and unset cells swapped
 */
function invert(mask: GridMask): GridMask {
  return { ...mask, data: mask.data.map((value) => 1 - value) };
}

/**
 * @param mask The mask to pad
 * @param margin Number of unset cells to add on each side
 * @returns the padded mask
 */
function pad(mask: GridMask, margin: number): GridMask {
  const width = mask.width + 2 * margin;
  const height = mask.height + 2 * margin;
  const data = new Uint8Array(width * height);
  for (let y = 0; y < mask.height; y++) {
    data.set(
      mask.data.subarray(y * mask.width, (y + 1) * mask.width),
      (y + margin) * width + margin,
    );
  }
  return { width, height, data };
}

/**
 * @param mask The mask to crop
 * @param margin Number of cells to remove on each side
 * @returns the cropped mask
 */
function crop(mask: GridMask, margin: number): GridMask {
  const width = mask.width - 2 * margin;
  const height = mask.height - 2 * margin;
  const data = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const start = (y + margin) * mask.width + margin;
    data.set(mask.data.subarray(start, start + width), y * width);
  }
  return { width, height, data };
}

/**
 * Fills the gaps narrower than about twice the radius, e.g. unscanned floor under furniture
 *
 * @param mask The mask to close
 * @param radius Radius of the square neighborhood, in cells
 * @returns the closed mask
 */
export function close(mask: GridMask, radius: number): GridMask {
  // The margin keeps the grid border from stopping the erosion
  const margin = 2 * radius;
  const grown = dilate(pad(mask, margin), radius);
  return crop(invert(dilate(invert(grown), radius)), margin);
}

/**
 * Visits the 4-connected components of the cells with a given value
 *
 * @param mask The mask to visit
 * @param value The value of the cells to group
 * @param visit Called with the cell indices of each component
 */
export function forEachComponent(
  mask: GridMask,
  value: 0 | 1,
  visit: (cells: number[]) => void,
): void {
  const { width, height, data } = mask;
  const seen = new Uint8Array(data.length);
  for (let start = 0; start < data.length; start++) {
    if (seen[start] || data[start] !== value) continue;
    const cells = [start];
    seen[start] = 1;
    // Visits the cells added while iterating too
    for (const cell of cells) {
      const x = cell % width;
      const neighbors = [
        x > 0 ? cell - 1 : -1,
        x < width - 1 ? cell + 1 : -1,
        cell - width,
        cell + width,
      ];
      for (const next of neighbors) {
        if (next < 0 || next >= width * height) continue;
        if (seen[next] || data[next] !== value) continue;
        seen[next] = 1;
        cells.push(next);
      }
    }
    visit(cells);
  }
}

/**
 * @param cells Cell indices
 * @param width Width of the grid
 * @param height Height of the grid
 * @returns true if one of the cells is on the border of the grid
 */
export function touchesBorder(
  cells: number[],
  width: number,
  height: number,
): boolean {
  return cells.some((cell) => {
    const x = cell % width;
    const y = Math.floor(cell / width);
    return x === 0 || y === 0 || x === width - 1 || y === height - 1;
  });
}

/**
 * Fills the enclosed holes up to a size, e.g. unscanned closets, and keeps the larger ones, e.g. courtyards
 *
 * @param mask The mask to fill
 * @param maxHoleCells Largest hole to fill, in cells
 * @param isFillable Tells if a hole can be filled, all of them by default
 * @returns the filled mask
 */
export function fillHoles(
  mask: GridMask,
  maxHoleCells: number,
  isFillable: (cells: number[]) => boolean = () => true,
): GridMask {
  const data = mask.data.slice();
  forEachComponent(mask, 0, (cells) => {
    if (
      cells.length <= maxHoleCells &&
      !touchesBorder(cells, mask.width, mask.height) &&
      isFillable(cells)
    ) {
      for (const cell of cells) data[cell] = 1;
    }
  });
  return { ...mask, data };
}
